import { useState } from "react";
import { hasNonLatin } from "@/lib/latinName";
import { type PersonProfileFields } from "@/api/profile";
import {
  type ProfileFieldsValue,
  type ProfileFieldsErrors,
} from "@/components/profile/ProfileFields";

/** A stored name is usable if present and Latin. */
function nameIsValid(p: PersonProfileFields): boolean {
  return (
    !!p.first_name?.trim() &&
    !!p.last_name?.trim() &&
    !hasNonLatin(p.first_name) &&
    !hasNonLatin(p.last_name)
  );
}

/**
 * State + validity for the inline profile fields, owned by the parent so a
 * single Register action can save then register. Renders the same ProfileFields
 * as the Profile page. New accounts always confirm; returning accounts see the
 * gate only when something's missing. Location is prefilled from the event.
 */
export function useProfileGate(
  person: PersonProfileFields | null,
  confirmAll: boolean,
  eventCity: string | null,
  eventCountry: string | null,
) {
  const nameOkStored = person ? nameIsValid(person) : false;
  const genderOkStored = !!person?.gender;

  const [value, setValue] = useState<ProfileFieldsValue>({
    firstName: person?.first_name ?? "",
    lastName: person?.last_name ?? "",
    gender: person?.gender ?? "unspecified",
    // Prefill home location from the event being registered for; keep any
    // existing home location the person already has.
    homeCity: person?.home_city ?? eventCity ?? "",
    homeCountry: person?.home_city ? person.home_country : eventCountry,
    phone: person?.phone ?? "",
  });
  const [showErrors, setShowErrors] = useState(false);

  const patch = (p: Partial<ProfileFieldsValue>) =>
    setValue((v) => ({ ...v, ...p }));

  const firstOk = value.firstName.trim().length > 0 && !hasNonLatin(value.firstName);
  const lastOk = /\p{L}/u.test(value.lastName.trim()) && !hasNonLatin(value.lastName);
  const genderOk = value.gender !== "unspecified";

  const visible = confirmAll || !nameOkStored || !genderOkStored;
  const valid = firstOk && lastOk && genderOk;

  // Only surface field errors once a submit was attempted.
  const errors: ProfileFieldsErrors = showErrors
    ? {
        firstName: !firstOk,
        lastName: !lastOk,
        gender: !genderOk,
      }
    : {};

  return { visible, valid, value, patch, errors, setShowErrors };
}

export type ProfileGateState = ReturnType<typeof useProfileGate>;
